import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const PredictiveChartsPanel = ({ timeHorizon, confidenceLevel }) => {
  const [chartType, setChartType] = useState('hiring-trends');

  const chartTypeOptions = [
    { value: 'hiring-trends', label: 'Hiring Trends Forecast' },
    { value: 'success-probability', label: 'Success Probability' },
    { value: 'salary-projection', label: 'Salary Projections' }
  ];

  const hiringTrendForecast = [
    { month: 'Feb 2026', actual: 225, predicted: 225, upperBound: 235, lowerBound: 215 },
    { month: 'Mar 2026', actual: null, predicted: 245, upperBound: 265, lowerBound: 225 },
    { month: 'Apr 2026', actual: null, predicted: 268, upperBound: 295, lowerBound: 241 },
    { month: 'May 2026', actual: null, predicted: 285, upperBound: 318, lowerBound: 252 },
    { month: 'Jun 2026', actual: null, predicted: 298, upperBound: 335, lowerBound: 261 },
    { month: 'Jul 2026', actual: null, predicted: 312, upperBound: 352, lowerBound: 272 }
  ];

  const successProbabilityData = [
    { month: 'Feb 2026', probability: 87.5, confidence: 95 },
    { month: 'Mar 2026', probability: 88.2, confidence: 94 },
    { month: 'Apr 2026', probability: 89.1, confidence: 93 },
    { month: 'May 2026', probability: 89.8, confidence: 92 },
    { month: 'Jun 2026', probability: 90.3, confidence: 91 },
    { month: 'Jul 2026', probability: 90.9, confidence: 90 }
  ];

  const salaryProjectionData = [
    { month: 'Feb 2026', avgSalary: 7.2, predicted: 7.2 },
    { month: 'Mar 2026', avgSalary: null, predicted: 7.5 },
    { month: 'Apr 2026', avgSalary: null, predicted: 7.8 },
    { month: 'May 2026', avgSalary: null, predicted: 8.1 },
    { month: 'Jun 2026', avgSalary: null, predicted: 8.4 },
    { month: 'Jul 2026', avgSalary: null, predicted: 8.7 }
  ];

  const getChartData = () => {
    switch (chartType) {
      case 'success-probability':
        return successProbabilityData;
      case 'salary-projection':
        return salaryProjectionData;
      default:
        return hiringTrendForecast;
    }
  };

  const renderChart = () => {
    const data = getChartData();

    if (chartType === 'hiring-trends') {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorPredicted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
            <YAxis stroke="var(--color-muted-foreground)" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'var(--color-card)', 
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="actual" 
              stroke="var(--color-success)" 
              fillOpacity={1} 
              fill="url(#colorActual)" 
              name="Actual Placements"
            />
            <Area 
              type="monotone" 
              dataKey="predicted" 
              stroke="var(--color-primary)" 
              fillOpacity={1} 
              fill="url(#colorPredicted)" 
              strokeDasharray="5 5"
              name="Predicted Placements"
            />
            <Area 
              type="monotone" 
              dataKey="upperBound" 
              stroke="var(--color-accent)" 
              fillOpacity={0.1} 
              fill="var(--color-accent)" 
              strokeDasharray="3 3"
              name="Upper Bound"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'success-probability') {
      return (
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorProbability" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-success)" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="var(--color-success)" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
            <YAxis stroke="var(--color-muted-foreground)" domain={[80, 95]} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'var(--color-card)', 
                border: '1px solid var(--color-border)',
                borderRadius: '8px'
              }} 
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="probability" 
              stroke="var(--color-success)" 
              fillOpacity={1} 
              fill="url(#colorProbability)" 
              name="Success Probability %"
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="colorSalary" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--color-warning)" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="var(--color-warning)" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
          <YAxis stroke="var(--color-muted-foreground)" />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'var(--color-card)', 
              border: '1px solid var(--color-border)',
              borderRadius: '8px'
            }} 
          />
          <Legend />
          <Area 
            type="monotone" 
            dataKey="predicted" 
            stroke="var(--color-warning)" 
            fillOpacity={1} 
            fill="url(#colorSalary)" 
            name="Predicted Avg. Salary (LPA)"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="LineChart" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-heading font-semibold text-card-foreground">Predictive Forecasting</h3>
        </div>
        <Select
          options={chartTypeOptions}
          value={chartType}
          onChange={setChartType}
          className="w-56"
        />
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">Predicted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-muted-foreground">Actual</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Info" size={14} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">{confidenceLevel}% Confidence Interval</span>
          </div>
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

export default PredictiveChartsPanel;